import { useCallback, useState } from 'react';

import {
  useRegisterUnsavedChanges,
  type UseRegisterUnsavedChangesOptions,
} from '../useRegisterUnsavedChanges';

/** Options for {@link useFormDirtyState}. */
export type UseFormDirtyStateOptions = Omit<UseRegisterUnsavedChangesOptions, 'isDirty'>;

/** Return value of {@link useFormDirtyState}. */
export interface UseFormDirtyStateResult {
  /** Whether the form has unsaved local changes. */
  isDirty: boolean;
  /** Mark the form as dirty (e.g. wire to `onValuesChange` / `onChange`). */
  markDirty: () => void;
  /** Reset dirty tracking after submit or explicit reset. */
  resetDirty: () => void;
}

/**
 * Tracks form dirty state and registers with the global unsaved-changes guard.
 *
 * @example
 * const { markDirty, resetDirty } = useFormDirtyState({
 *   scope: UnsavedChangesScopeEnum.Modal,
 *   onDiscard: resetFormToInitial,
 * });
 * // <form onChange={markDirty} /> or onValuesChange={markDirty}
 */
export function useFormDirtyState({
  scope,
  onDiscard,
  enabled = true,
}: UseFormDirtyStateOptions): UseFormDirtyStateResult {
  const [isDirty, setIsDirty] = useState(false);

  useRegisterUnsavedChanges({
    enabled,
    isDirty,
    onDiscard,
    scope,
  });

  const markDirty = useCallback(() => {
    if (!enabled) {
      return;
    }

    setIsDirty(true);
  }, [enabled]);

  const resetDirty = useCallback(() => {
    setIsDirty(false);
  }, []);

  return {
    isDirty,
    markDirty,
    resetDirty,
  };
}
